import { useEffect, useLayoutEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import type { LoanListItem } from '@app/shared';
import type { Column } from './columns';
import {
  OPERATOR_LABELS, applyFilters, defaultOperator, isMultiSelect, newFilterKey,
  operatorsFor, summarize, takesNoValue, valueOptions, withSelectedOptions,
  type Filter, type Operator, type ValueOption,
} from './filterModel';

// ---------------------------------------------------------------------------
// The filter bar: a quick "any field" box, one chip per active filter, and an
// "+ Add filter" popover. Chips are re-openable — clicking one edits it in the
// same panel that created it.
// ---------------------------------------------------------------------------

type Props = {
  /** Every loaded row, before filtering — the pool counts are drawn from. */
  rows: LoanListItem[];
  /** Columns offered in the field dropdown. */
  fields: Column[];
  filters: Filter[];
  onChange: (next: Filter[]) => void;
  quick: string;
  onQuickChange: (v: string) => void;
  /**
   * Restores this table's starting filters (e.g. "Analyst equals <you>") and
   * clears the quick search. Always offered, including when nothing is
   * filtered — that's the only way back to a default chip you've removed.
   */
  onReset: () => void;
  /** Right-aligned content on the same row — the record count, scope toggles. */
  trailing?: ReactNode;
};

export function FilterBar({ rows, fields, filters, onChange, quick, onQuickChange, onReset, trailing }: Props) {
  // Which filter the popover is editing: a key for an existing chip, 'new', or null (closed).
  const [editing, setEditing] = useState<string | 'new' | null>(null);
  const barRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!editing) return;
    const onDown = (e: MouseEvent) => {
      if (barRef.current && !barRef.current.contains(e.target as Node)) setEditing(null);
    };
    const onEsc = (e: KeyboardEvent) => { if (e.key === 'Escape') setEditing(null); };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onEsc);
    return () => { document.removeEventListener('mousedown', onDown); document.removeEventListener('keydown', onEsc); };
  }, [editing]);

  const columnOf = (id: string) => fields.find(c => c.id === id);

  const remove = (key: string) => {
    onChange(filters.filter(f => f.key !== key));
    if (editing === key) setEditing(null);
  };

  const commit = (f: Filter) => {
    const exists = filters.some(x => x.key === f.key);
    onChange(exists ? filters.map(x => x.key === f.key ? f : x) : [...filters, f]);
    setEditing(null);
  };

  const draft = editing && editing !== 'new' ? filters.find(f => f.key === editing) : undefined;

  // One editor instance, rendered inside whichever anchor is currently open.
  // Keyed on `editing` so switching chips resets its internal draft state.
  const editor = editing ? (
    <FilterEditor
      key={editing}
      fields={fields}
      rows={rows}
      filters={filters}
      columnOf={columnOf}
      initial={draft}
      onApply={commit}
      onCancel={() => setEditing(null)}
    />
  ) : null;

  return (
    <div className="filter-bar" ref={barRef}>
      <div className="filter-bar-row">
        <input
          type="search"
          className="quick-filter"
          placeholder="Filter for any field…"
          value={quick}
          onChange={e => onQuickChange(e.target.value)}
        />

        {filters.map(f => {
          const c = columnOf(f.columnId);
          if (!c) return null;
          // Labels are normally captured on Apply. Only fall back to a lookup
          // when one is missing, and search the *whole* loaded set rather than
          // the filtered pool — a sibling filter must not be able to erase the
          // name this chip displays.
          const needsLookup = isMultiSelect(f.op, c.kind)
            && f.values.some(v => !f.labels?.[v]);
          const opts = needsLookup ? valueOptions(c, rows) : [];
          const { field, op, value } = summarize(c, f, opts);
          return (
            // The popover is a child of the control that opened it, so it drops
            // directly beneath that chip rather than off at the page edge.
            <span key={f.key} className="filter-anchor">
              <span className={`filter-chip${editing === f.key ? ' open' : ''}`}>
                <button type="button" className="chip-label" onClick={() => setEditing(editing === f.key ? null : f.key)}>
                  <span className="chip-field">{field}</span>
                  {' '}<span className="chip-op">{op}</span>
                  {value && <>{' '}<span className="chip-value">{value}</span></>}
                </button>
                <button type="button" className="chip-remove" aria-label={`Remove ${c.header} filter`} onClick={() => remove(f.key)}>×</button>
              </span>
              {editing === f.key && editor}
            </span>
          );
        })}

        <span className="filter-anchor">
          <button
            type="button"
            className={`add-filter${editing === 'new' ? ' open' : ''}`}
            onClick={() => setEditing(editing === 'new' ? null : 'new')}
          >
            + Add filter
          </button>
          {editing === 'new' && editor}
        </span>

        <button
          type="button"
          className="btn-sm clear-filters"
          onClick={() => { onReset(); setEditing(null); }}
        >
          Reset to default
        </button>

        {trailing && <span className="filter-bar-trailing">{trailing}</span>}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------

function FilterEditor({ fields, rows, filters, columnOf, initial, onApply, onCancel }: {
  fields: Column[];
  rows: LoanListItem[];
  filters: Filter[];
  columnOf: (id: string) => Column | undefined;
  initial?: Filter;
  onApply: (f: Filter) => void;
  onCancel: () => void;
}) {
  const [columnId, setColumnId] = useState(initial?.columnId ?? fields[0]?.id ?? '');
  const column = fields.find(c => c.id === columnId) ?? fields[0];
  const [op, setOp] = useState<Operator>(initial?.op ?? (column ? defaultOperator(column) : 'equals'));
  const [values, setValues] = useState<string[]>(initial?.values ?? []);
  const [valueSearch, setValueSearch] = useState('');

  // A chip near the right edge would otherwise open a panel that runs off the
  // page, so measure once on open and right-align it instead.
  const panelRef = useRef<HTMLDivElement>(null);
  const [flip, setFlip] = useState(false);
  useLayoutEffect(() => {
    const el = panelRef.current;
    if (el && el.getBoundingClientRect().right > window.innerWidth - 8) setFlip(true);
  }, []);

  // Counts exclude this filter's own contribution so the numbers show what
  // each choice would yield, not what the current selection already yields.
  const pool = useMemo(
    () => applyFilters(rows, filters, columnOf, initial?.key),
    [rows, filters, columnOf, initial?.key],
  );
  // The values this chip arrived with, frozen for the editor's lifetime. Used to keep a
  // selected-but-unmatched value in the list even after it is unticked, so it does not
  // disappear from under the pointer.
  const openedWith = useMemo(() => initial?.values ?? [], [initial]);
  const options = useMemo(
    () => column && isMultiSelect(op, column.kind)
      ? withSelectedOptions(valueOptions(column, pool), openedWith, column, initial?.labels)
      : [],
    [column, op, pool, openedWith, initial?.labels],
  );
  const shown = useMemo(() => {
    const q = valueSearch.trim().toLowerCase();
    return q ? options.filter(o => o.label.toLowerCase().includes(q)) : options;
  }, [options, valueSearch]);

  if (!column) return null;

  const changeColumn = (id: string) => {
    const next = fields.find(c => c.id === id)!;
    setColumnId(id);
    setOp(defaultOperator(next));
    setValues([]);
    setValueSearch('');
  };

  const changeOp = (next: Operator) => {
    // Value shapes differ between multi-select and the scalar operators, so
    // switching between those families resets rather than carrying junk over.
    if (isMultiSelect(next, column.kind) !== isMultiSelect(op, column.kind)) setValues([]);
    setOp(next);
  };

  const toggle = (v: string) =>
    setValues(vs => vs.includes(v) ? vs.filter(x => x !== v) : [...vs, v]);

  const allShownSelected = shown.length > 0 && shown.every(o => values.includes(o.value));
  const toggleAll = () =>
    setValues(allShownSelected ? [] : shown.map(o => o.value));

  const apply = () => {
    // Freeze the chosen values' labels now, while the options that produced
    // them are still on screen. Re-deriving later can fail: sibling filters may
    // leave no row carrying the value, and the chip would show a raw OID.
    const labels: Record<string, string> = { ...initial?.labels };
    for (const v of values) {
      const hit = options.find(o => o.value === v);
      if (hit) labels[v] = hit.label;
      else if (v === '' && column.emptyLabel) labels[v] = column.emptyLabel;
    }
    onApply({ key: initial?.key ?? newFilterKey(), columnId, op, values, labels });
  };

  return (
    <div className={`filter-popover${flip ? " flip" : ""}`} ref={panelRef}>
      <div className="filter-popover-title">Filter results</div>

      <label className="filter-field">
        <span>Filter</span>
        <select value={columnId} onChange={e => changeColumn(e.target.value)}>
          {fields.map(c => <option key={c.id} value={c.id}>{c.header}</option>)}
        </select>
      </label>

      <label className="filter-field">
        <span>Operator</span>
        <select value={op} onChange={e => changeOp(e.target.value as Operator)}>
          {operatorsFor(column).map(o => <option key={o} value={o}>{OPERATOR_LABELS[o]}</option>)}
        </select>
      </label>

      {!takesNoValue(op) && (
        <div className="filter-field">
          <span>Value</span>
          {isMultiSelect(op, column.kind) ? (
            <ValueChecklist
              options={shown}
              values={values}
              search={valueSearch}
              onSearch={setValueSearch}
              allSelected={allShownSelected}
              onToggleAll={toggleAll}
              onToggle={toggle}
            />
          ) : op === 'between' ? (
            <div className="filter-range">
              <input
                type={column.kind === 'date' ? 'date' : 'number'}
                value={values[0] ?? ''}
                onChange={e => setValues(v => [e.target.value, v[1] ?? ''])}
              />
              <span className="range-sep">to</span>
              <input
                type={column.kind === 'date' ? 'date' : 'number'}
                value={values[1] ?? ''}
                onChange={e => setValues(v => [v[0] ?? '', e.target.value])}
              />
            </div>
          ) : (
            <input
              autoFocus
              type={column.kind === 'date' ? 'date' : column.kind === 'money' ? 'number' : 'text'}
              placeholder={column.kind === 'text' ? 'Type a value' : undefined}
              value={values[0] ?? ''}
              onChange={e => setValues([e.target.value])}
              onKeyDown={e => { if (e.key === 'Enter') apply(); }}
            />
          )}
        </div>
      )}

      <div className="filter-popover-actions">
        <button type="button" className="btn-sm btn-primary" onClick={apply}>Apply</button>
        <button type="button" className="btn-sm" onClick={onCancel}>Cancel</button>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------

function ValueChecklist({ options, values, search, onSearch, allSelected, onToggleAll, onToggle }: {
  options: ValueOption[];
  values: string[];
  search: string;
  onSearch: (v: string) => void;
  allSelected: boolean;
  onToggleAll: () => void;
  onToggle: (v: string) => void;
}) {
  return (
    <>
      <input
        type="search"
        className="value-search"
        placeholder="Search values"
        value={search}
        onChange={e => onSearch(e.target.value)}
      />
      <div className="value-list">
        {options.length === 0 ? (
          <p className="value-empty">No results</p>
        ) : (
          <>
            <label className="value-option">
              <input type="checkbox" checked={allSelected} onChange={onToggleAll} />
              <span className="value-label">All</span>
            </label>
            {options.map(o => (
              <label key={o.value} className="value-option">
                <input type="checkbox" checked={values.includes(o.value)} onChange={() => onToggle(o.value)} />
                <span className="value-label">{o.label}</span>
                <span className="value-count">({o.count})</span>
              </label>
            ))}
          </>
        )}
      </div>
    </>
  );
}
